import type { Dispatch, SetStateAction } from "react";
import { adminPost } from "@/lib/kbAdminClient";
import type { KbAudience, KbScope, KbSplitProposalItem, PanelNote } from "./types";

export async function proposeKbDocumentSplit(ctx: {
  articleContent: string;
  articleTitle: string;
  articleDocType: string;
  setSplitWorking: Dispatch<SetStateAction<boolean>>;
  setSplitOpen: Dispatch<SetStateAction<boolean>>;
  setSplitProposals: Dispatch<SetStateAction<KbSplitProposalItem[]>>;
  showNote: (n: PanelNote | null) => void;
}): Promise<void> {
  const { articleContent, articleTitle, articleDocType, setSplitWorking, setSplitOpen, setSplitProposals, showNote } = ctx;
  if (!articleContent.trim()) return;
  setSplitWorking(true);
  setSplitOpen(true);
  setSplitProposals([]);
  try {
    const response = await adminPost<{ ok: true; proposals: any[] }>("/api/kb/admin/splitProposal", {
      content: articleContent,
      title: articleTitle,
    });
    if (!response.ok) {
      showNote({ tone: "error", title: "Split analysis failed", text: response.error });
      return;
    }
    const items = (response.data.proposals || []).map((p: any) => ({
      title: typeof p.title === "string" ? p.title : "Untitled",
      body: typeof p.body === "string" ? p.body : "",
      doc_type: typeof p.doc_type === "string" ? p.doc_type : articleDocType || "",
      reason: typeof p.reason === "string" ? p.reason : "",
      checked: true,
      created: false,
    }));
    setSplitProposals(items);
    if (!items.length) {
      showNote({ tone: "info", title: "No split proposals", text: "The AI did not find clear section boundaries in this article." });
    }
  } finally {
    setSplitWorking(false);
  }
}

export async function createKbSplitDraftArticles(ctx: {
  splitProposals: KbSplitProposalItem[];
  hasEditorName: boolean;
  editorName: string;
  articleAudience: KbAudience;
  articleScope: KbScope;
  articleDocType: string;
  articleTitle: string;
  articleSlug: string;
  setSplitCreating: (v: boolean) => void;
  setSplitProposals: (fn: (prev: KbSplitProposalItem[]) => KbSplitProposalItem[]) => void;
  showNote: (n: PanelNote | null) => void;
  refreshDraftCount: () => void | Promise<void>;
}): Promise<void> {
  const {
    splitProposals,
    hasEditorName,
    editorName,
    articleAudience,
    articleScope,
    articleDocType,
    articleTitle,
    articleSlug,
    setSplitCreating,
    setSplitProposals,
    showNote,
    refreshDraftCount,
  } = ctx;

  const toCreate = splitProposals.filter((p) => p.checked && !p.created);
  if (!toCreate.length || !hasEditorName) return;
  setSplitCreating(true);
  let createdCount = 0;
  for (const proposal of toCreate) {
    const response = await adminPost<any>("/api/kb/admin/article", {
      mode: "save",
      title: proposal.title,
      content: proposal.body,
      audience: articleAudience,
      scope: articleScope,
      doc_type: proposal.doc_type || articleDocType || null,
      tags: [],
      locations: [],
      sourceUrl: null,
      status: "draft",
      chunkSize: 500,
      overlap: 50,
      editedBy: editorName,
    });
    if (response.ok) {
      setSplitProposals((prev) => prev.map((p) => (p === proposal ? { ...p, created: true } : p)));
      createdCount++;
    } else {
      showNote({ tone: "error", title: `Failed to create "${proposal.title}"`, text: response.error });
    }
  }
  setSplitCreating(false);
  if (createdCount > 0) {
    showNote({
      tone: "success",
      title: `${createdCount} draft article${createdCount === 1 ? "" : "s"} created`,
      text: `Find ${createdCount === 1 ? "it" : "them"} in Library → Show drafts. The original article "${articleTitle || articleSlug}" has not been modified — update or delete it separately once you've finished the split articles.`,
    });
    void refreshDraftCount();
  }
}
